// Razorpay Payment Service
import { auth } from '../firebase';

/** Plan being purchased — the server prices the order from this (never from a client amount). */
export interface RazorpayPlanSelection {
  plan: 'pro' | 'business';
  subscribers: number;
  billingPeriod: 'monthly' | 'annual';
}

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to pay.');
  const token = await user.getIdToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export interface RazorpayOptions {
  key: string;
  amount: number; // Amount in paise (smallest currency unit)
  currency: string;
  name: string;
  description: string;
  order_id?: string;
  handler: (response: RazorpaySuccessResponse) => void;
  prefill: {
    name?: string;
    email?: string;
    contact?: string;
  };
  notes?: Record<string, string>;
  theme?: {
    color: string;
  };
  modal?: {
    ondismiss?: () => void;
  };
}

export interface RazorpaySuccessResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface RazorpayErrorResponse {
  error: {
    code: string;
    description: string;
    source: string;
    step: string;
    reason: string;
    metadata: Record<string, any>;
  };
}

export interface CreateOrderResponse {
  orderId: string;
  amount: number;
  currency: string;
  keyId?: string; // Key ID used to create the order
}

class RazorpayService {
  private baseUrl: string;
  private keyId: string | null = null;

  constructor() {
    // Always use Firebase Functions URL for Razorpay (don't use localhost fallback)
    // This ensures payments work in production even if REACT_APP_API_BASE_URL is set to localhost
    const firebaseProjectId = 'formulabot-1d12b';
    const functionsRegion = 'us-central1';
    const functionsUrl = `https://${functionsRegion}-${firebaseProjectId}.cloudfunctions.net`;

    // For Razorpay, always use the production Firebase Functions URL
    // Unless explicitly set to a different cloud functions URL (not localhost)
    const envUrl = process.env.REACT_APP_API_BASE_URL || '';
    const isLocalhost = envUrl.includes('localhost') || envUrl.includes('127.0.0.1');
    this.baseUrl = isLocalhost ? functionsUrl : (envUrl || functionsUrl);

    console.log('💳 Razorpay baseUrl:', this.baseUrl);
    this.keyId = process.env.REACT_APP_RAZORPAY_KEY_ID || null;

    // Debug: Log which key is being loaded
    if (this.keyId) {
      console.log('🔑 Razorpay Key ID loaded:', this.keyId.substring(0, 15) + '... (length: ' + this.keyId.length + ')');
    } else {
      console.warn('⚠️ Razorpay Key ID NOT loaded from environment variables');
    }
  }

  /**
   * Load Razorpay script dynamically
   */
  async loadRazorpay(): Promise<boolean> {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  /**
   * Create a Razorpay order (backend call)
   */
  async createOrder(selection: RazorpayPlanSelection, currency: string): Promise<CreateOrderResponse> {
    try {
      // SECURITY: the backend computes the amount from the plan selection.
      const endpoint = `${this.baseUrl}/createRazorpayOrder`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          plan: selection.plan,
          subscribers: selection.subscribers,
          billingPeriod: selection.billingPeriod,
          currency: currency.toUpperCase(),
        }),
      });

      if (!response.ok) {
        let errorMessage = `Failed to create order: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.details) {
            errorMessage = errorData.details;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // Ignore JSON parsing error
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.orderId) {
        throw new Error('Invalid response from server');
      }

      // Log the response for debugging
      console.log('Razorpay order creation response:', {
        orderId: data.orderId,
        amount: data.amount,
        currency: data.currency,
        hasKeyId: !!data.keyId,
        keyIdPreview: data.keyId ? data.keyId.substring(0, 15) + '...' : 'missing',
      });

      return {
        orderId: data.orderId,
        amount: data.amount, // Already in paise from backend
        currency: data.currency || currency.toUpperCase(),
        keyId: data.keyId, // Key ID used to create the order (must match for checkout)
      };
    } catch (error) {
      console.error('Error creating Razorpay order:', error);
      if (error instanceof Error) {
        throw new Error(error.message || 'Failed to create Razorpay order');
      }
      throw new Error('Failed to create Razorpay order');
    }
  }

  /**
   * Verify payment signature (backend call)
   */
  async verifyPayment(paymentId: string, orderId: string, signature: string): Promise<boolean> {
    try {
      // Use Firebase Functions endpoint directly
      // The backend verifies the payment and activates the plan server-side.
      const endpoint = `${this.baseUrl}/verifyRazorpayPayment`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          razorpay_payment_id: paymentId,
          razorpay_order_id: orderId,
          razorpay_signature: signature,
        }),
      });

      if (!response.ok) {
        throw new Error(`Payment verification failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data.verified === true;
    } catch (error) {
      console.error('Error verifying payment:', error);
      return false;
    }
  }

  /**
   * Open Razorpay checkout
   */
  async openCheckout(options: RazorpayOptions): Promise<void> {
    await this.loadRazorpay();

    if (!(window as any).Razorpay) {
      throw new Error('Razorpay SDK failed to load');
    }

    // Use the key from options if provided, otherwise use service keyId
    const keyId = options.key || this.keyId;

    if (!keyId) {
      console.error('Razorpay key ID check:', {
        fromOptions: !!options.key,
        fromService: !!this.keyId,
        optionsKeyPreview: options.key ? `${options.key.substring(0, 15)}...` : 'missing',
        serviceKeyPreview: this.keyId ? `${this.keyId.substring(0, 15)}...` : 'missing'
      });
      throw new Error('Razorpay key ID not configured');
    }

    console.log('Opening Razorpay checkout with key:', keyId.substring(0, 15) + '...');

    // Build Razorpay options - use options passed in, but ensure key is set
    const razorpayOptions: any = {
      key: keyId,
      amount: options.amount,
      currency: options.currency,
      name: options.name,
      description: options.description,
      order_id: options.order_id,
      handler: (response: any) => {
        console.log('Razorpay payment handler called:', response);
        options.handler(response);
      },
      prefill: options.prefill || {},
      notes: options.notes || {},
      theme: options.theme || { color: '#7c3aed' },
      modal: options.modal || {
        ondismiss: () => {
          console.log('Razorpay checkout closed');
        },
      },
    };

    // Only add method if it's provided in options (to avoid conflicts)
    if ((options as any).method) {
      razorpayOptions.method = (options as any).method;
    }

    console.log('Creating Razorpay instance with options:', {
      key: keyId.substring(0, 15) + '...',
      amount: razorpayOptions.amount,
      amountInRupees: (razorpayOptions.amount / 100).toFixed(2) + ' ' + razorpayOptions.currency,
      currency: razorpayOptions.currency,
      order_id: razorpayOptions.order_id,
    });

    // Validate order_id format (Razorpay order IDs start with 'order_')
    if (razorpayOptions.order_id && !razorpayOptions.order_id.startsWith('order_')) {
      console.warn('⚠️ Warning: Order ID format may be incorrect:', razorpayOptions.order_id);
    }

    const razorpay = new (window as any).Razorpay(razorpayOptions);

    // Add error event listener
    razorpay.on('payment.failed', function (response: any) {
      console.error('❌ Razorpay payment failed:', response);
      const errorDetails = {
        code: response.error?.code,
        description: response.error?.description,
        source: response.error?.source,
        step: response.error?.step,
        reason: response.error?.reason,
        metadata: response.error?.metadata,
      };
      console.error('Payment failure details:', errorDetails);

      // Show user-friendly error message
      let userMessage = 'Payment processing failed. ';
      if (response.error?.description) {
        userMessage += response.error.description;
      } else if (response.error?.reason) {
        userMessage += response.error.reason;
      } else {
        userMessage += 'Please try again or use a different payment method.';
      }

      // You can trigger a callback here if needed
      // options.onError?.(userMessage);
    });

    razorpay.open();
  }

  /**
   * Check if Razorpay is configured
   */
  isConfigured(): boolean {
    return !!this.keyId;
  }
}

export const razorpayService = new RazorpayService();

// Extend window interface for Razorpay
declare global {
  interface Window {
    Razorpay: any;
  }
}

